import { useEffect, useState } from "react";
import { toast } from "sonner";
import { FileText } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";

const fill = (t, lead, agent) => t.replace(/\{name\}/g, lead.contact_name || "there").replace(/\{company\}/g, lead.company_name || "your company")
  .replace(/\{agent\}/g, agent).replace(/\{requirement\}/g, lead.requirement || "your requirement");

const openWa = (w, phone, text, links) => {
  const extra = links.map((l) => `${l.name}: ${process.env.REACT_APP_BACKEND_URL}${l.url_path}`).join("\n");
  const url = `https://wa.me/${phone}?text=${encodeURIComponent(extra ? `${text}\n\n${extra}` : text)}`;
  if (w) w.location.href = url; else window.location.href = url;
};

export const missedCall = (lead, agent, onDone) => {
  const w = window.open("", "_blank");
  api.get("/whatsapp/config").then((c) => {
    const t = c.data.templates[0];
    if (!t) throw new Error("Ask an admin to add a message under WhatsApp & Files");
    const text = fill(t.text, lead, agent);
    return api.post(`/leads/${lead.lead_id}/whatsapp`, { text, file_ids: [], template_id: t.template_id, missed_call: true }).then((r) => { openWa(w, r.data.phone, text, r.data.links); return r.data.follow_up; });
  }).then((f) => {
    const when = f?.next_call_at ? `Call back reminder at ${new Date(f.next_call_at).toLocaleTimeString("en-IN", { timeZone: "Asia/Kolkata", hour: "numeric", minute: "2-digit" })}` : "3rd try today, follow-up moved to tomorrow";
    toast.success(`Attempt ${f?.attempt} logged. ${when}. Tap send in WhatsApp.`); window.dispatchEvent(new Event("callbacks-changed")); onDone();
  })
    .catch((e) => { if (w) w.close(); toast.error(e.response ? errMsg(e) : e.message); });
};

export const WhatsAppDialog = ({ lead, onClose, onDone }) => {
  const { user } = useAuth();
  const agent = user?.staff_name || user?.name || "";
  const [cfg, setCfg] = useState(null);
  const [tpl, setTpl] = useState("");
  const [text, setText] = useState("");
  const [files, setFiles] = useState([]);
  useEffect(() => {
    api.get("/whatsapp/config").then((r) => { setCfg(r.data); const t = r.data.templates[0]; if (t) { setTpl(t.template_id); setText(fill(t.text, lead, agent)); } }).catch((e) => toast.error(errMsg(e)));
  }, [lead, agent]);
  const pick = (id) => { setTpl(id); const t = cfg.templates.find((x) => x.template_id === id); if (t) setText(fill(t.text, lead, agent)); };
  const toggle = (id) => setFiles(files.includes(id) ? files.filter((x) => x !== id) : [...files, id]);
  const send = () => {
    const w = window.open("", "_blank");
    api.post(`/leads/${lead.lead_id}/whatsapp`, { text, file_ids: files, template_id: tpl || null }).then((r) => {
      openWa(w, r.data.phone, text, r.data.links);
      toast.success("WhatsApp opened. Tap send in WhatsApp.");
      onDone();
    }).catch((e) => { if (w) w.close(); toast.error(errMsg(e)); });
  };
  if (!cfg) return null;
  return (
    <Dialog open onOpenChange={(x) => !x && onClose()}><DialogContent className="bg-card max-w-lg" data-testid="whatsapp-dialog">
      <DialogHeader><DialogTitle className="font-display">WhatsApp {lead.contact_name || lead.company_name}</DialogTitle></DialogHeader>
      {cfg.templates.length > 1 && <select data-testid="whatsapp-template" value={tpl} onChange={(e) => pick(e.target.value)} className="w-full bg-muted/60 border h-9 px-2 text-sm">
        {cfg.templates.map((t) => <option key={t.template_id} value={t.template_id}>{t.name}</option>)}</select>}
      <textarea data-testid="whatsapp-text" value={text} onChange={(e) => setText(e.target.value)} className="w-full h-32 bg-muted/60 border p-2 text-sm" />
      <div className="ax-label">Attach from library</div>
      {!cfg.files.length ? <p className="text-xs text-muted-foreground" data-testid="whatsapp-no-files">No files yet. An admin can add them under Administration › WhatsApp & Files.</p> : (
        <div className="max-h-40 overflow-y-auto space-y-1" data-testid="whatsapp-files">
          {cfg.files.map((f) => <label key={f.file_id} className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" data-testid={`whatsapp-file-${f.file_id}`} checked={files.includes(f.file_id)} onChange={() => toggle(f.file_id)} />
            <FileText className="h-3.5 w-3.5 text-primary" />{f.name}</label>)}
        </div>)}
      <p className="text-xs text-muted-foreground">Files go as secure download links that work for 30 days. WhatsApp opens with the message ready; tap send.</p>
      <button data-testid="whatsapp-send" disabled={!text.trim()} onClick={send} className="bg-emerald-600 text-white h-9 text-sm disabled:opacity-50">Open WhatsApp</button>
    </DialogContent></Dialog>
  );
};
